// import components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Tooltip } from 'components/shared';
import { Icon } from '@sdb/planning-ui';
import Absence from './Absence';
import Duty from '../Duty';
import { EmployeeRowStyles } from './EmployeeRow.styles';

// import hooks
import { useTranslation } from 'react-i18next';

// import types
import { EmployeeRowProps } from './EmployeeRow.types';

// import utils
import { format } from 'date-fns';
import { sortBy } from 'lodash';
import { getHoursMinutesSeconds } from 'utils/date';
import { getCellDateClasses } from 'components/shared/roster/utils';

export default function EmployeeRow({
  employee,
  selectedPlanUnits,
  rosterDays,
  portal,
  interval
}: EmployeeRowProps) {
  const {
    benefit,
    nameInformal,
    plannedCostCenter,
    days,
    phoneNumber,
    smsNumber
  } = employee;
  const { t } = useTranslation('common');

  return (
    <EmployeeRowStyles className="employee-row">
      <TableCell>
        <Tooltip
          title={
            <Table size="small" sx={{ 'tr:last-child td': { border: 0 } }}>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <strong>{t('source')}</strong>
                  </TableCell>
                  <TableCell>{benefit.costCenter?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>{t('work')}</strong>
                  </TableCell>
                  <TableCell>{plannedCostCenter.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>{t('function')}</strong>
                  </TableCell>
                  <TableCell>{benefit.function?.name || '-'}</TableCell>
                </TableRow>
                {smsNumber && (
                  <TableRow>
                    <TableCell>
                      <strong>{t('mobileNumber')}</strong>
                    </TableCell>
                    <TableCell>{smsNumber}</TableCell>
                  </TableRow>
                )}
                {phoneNumber && (
                  <TableRow>
                    <TableCell>
                      <strong>{t('phoneNumber')}</strong>
                    </TableCell>
                    <TableCell>{phoneNumber}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          }
          arrow
          enterDelay={300}
          enterNextDelay={300}
          placement="right"
          disableInteractive
        >
          <div className="cell-content">
            <div className="function-container semibold">
              {benefit.function?.colorCode && (
                <span
                  className="function-color"
                  style={{ color: benefit.function.colorCode }}
                >
                  <Icon type="circle" color="inherit" fontSize="small" />
                </span>
              )}
              {nameInformal}
            </div>
            {selectedPlanUnits.length === 1 ? (
              selectedPlanUnits[0].id !== benefit.costCenter?.id && (
                <div>{benefit.costCenter?.name}</div>
              )
            ) : (
              <div className="cost-center-row croppedTextCell">
                <span>
                  {benefit.costCenter?.name}
                  {benefit.costCenter?.id !== plannedCostCenter?.id && (
                    <>
                      <Icon
                        type="chevron-right"
                        fontSize="small"
                        className="chevron-icon"
                      />
                      {plannedCostCenter?.name}
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        </Tooltip>
      </TableCell>
      {rosterDays.map(({ day, date }) => {
        const absences = days[format(date, 'yyyy-MM-dd')]?.absences;
        const duties = days[format(date, 'yyyy-MM-dd')]?.duties;

        return (
          <TableCell key={day} className={getCellDateClasses({ date })}>
            {absences &&
              sortBy(absences, [
                (absence) => getHoursMinutesSeconds(absence.start),
                (absence) => getHoursMinutesSeconds(absence.end)
              ]).map((absence) => (
                <Absence
                  key={absence.id}
                  absence={absence}
                  date={date}
                  interval={interval}
                />
              ))}
            {duties &&
              sortBy(duties, [
                (duty) => getHoursMinutesSeconds(duty.start),
                (duty) => getHoursMinutesSeconds(duty.end)
              ]).map((duty) => (
                <Duty key={duty.dutyLineId} duty={duty} portal={portal} />
              ))}
          </TableCell>
        );
      })}
    </EmployeeRowStyles>
  );
}
